import React, { useEffect, useRef } from 'react'
import cls from 'classnames'
import styled from 'styled-components'

import ActionIcon from 'components/lib/DesignSystem/ActionIcon'
import Tooltip from 'components/lib/DesignSystem/Tooltip'

import { Info } from '../Icons'

import Check from './icons/Check'

/** Icon / dash color on a filled (primary or `selectedColor`) box — not `var(--white)` (dark theme trap). */
const ON_FILL_ICON = 'var(--button-primary-text-default)'

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;

  .tooltip-container {
    align-items: center;
  }

  .checkbox-container {
    display: flex;
    align-items: center;
  }

  .checkbox-label-wrapper {
    display: flex;
    align-items: center;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    user-select: none;
  }

  .checkbox-custom {
    position: relative;
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    border: 1px solid
      ${(props) =>
        props.checked ? props.selectedColor || 'var(--brand-color)' : 'var(--ds-gray-4)'};
    border-radius: 2px;
    margin-right: 10px;
    background-color: ${(props) =>
      props.checked ? props.selectedColor || 'var(--primary)' : 'transparent'};
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      border-color: ${(props) =>
        props.selectedColor ? props.selectedColor : 'var(--ds-button-hover)'};
      background-color: ${(props) =>
        props.checked
          ? props.selectedColor
            ? props.selectedColor
            : 'var(--ds-button-hover)'
          : 'transparent'};
    }
  }

  .minus-icon {
    width: 8.5px;
    height: 1px;
    border-radius: 1px;
  }

  &.disabled {
    .checkbox-custom {
      border-color: var(--ds-gray-7);
      background-color: ${(props) => (props.checked ? 'var(--ds-gray-7)' : 'transparent')};
      pointer-events: none;
    }

    .checkbox-label {
      color: var(--ds-gray-5);
    }
  }

  .checkbox-label {
    color: var(--ds-gray-1);
  }

  .custom-checkbox {
    position: absolute;
    z-index: -1;
    opacity: 0;
    width: 0;
    height: 0;
    margin: 0;
    font-weight: 400;
    color: var(--ds-gray-1);
    position: relative;
    display: flex;
    align-items: center;
  }

  .checkbox-icon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }

  // For checkboxes inside dropdowns
  &.option-wrap__checkbox {
    align-items: center;
    label {
      top: 0;
    }
  }
  label {
    //escape bootstrap
    all: unset;
  }
`

/**
 * A customizable checkbox component that follows accessibility best practices.
 * It supports states like checked, unchecked, indeterminate, and disabled.
 *
 * @param {Object} props - Component props
 * @param {string} [props.id] - Unique identifier for the checkbox. If not provided, generated from name
 * @param {string} [props.className] - Additional CSS classes to apply
 * @param {boolean} props.checked - Whether the checkbox is checked
 * @param {string} [props.labelText] - Text label to display next to the checkbox
 * @param {boolean} [props.disabled] - Whether the checkbox is disabled
 * @param {boolean} [props.indeterminate] - Whether to show indeterminate state (partially checked)
 * @param {Function} props.onChange - Callback function when checkbox state changes
 * @param {string} [props.name] - Name attribute for the checkbox input
 * @param {React.ReactNode} [props.InfoPopupComponent] - Component to display additional information in a tooltip
 * @param {string} [props.tooltipId] - ID for the tooltip if InfoPopupComponent is used
 * @param {boolean} [props.preventDefault] - Whether to prevent default event handling
 * @param {boolean} [props.readOnly] - Whether the checkbox is read-only
 * @param {Object} [props.wrapperStyle] - Custom styles for the checkbox wrapper
 * @param {string} [props.selectedColor] - Custom color for the selected (checked) state
 */
const Checkbox = ({
  id,
  dataTestId,
  dataPendoFeature,
  className,
  checked,
  label,
  labelText = label,
  disabled,
  indeterminate,
  onChange,
  name,
  InfoPopupComponent,
  tooltipId,
  preventDefault,
  readOnly,
  wrapperStyle,
  selectedColor,
}) => {
  const checkboxId = id || `checkbox-${name}`
  const inputRef = useRef(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = !!indeterminate
    }
  }, [indeterminate])

  const minusIconBackgroundColor = disabled
    ? 'var(--ds-gray-5)'
    : checked
      ? ON_FILL_ICON
      : 'var(--color-text)'

  return (
    <CheckboxWrapper
      selectedColor={selectedColor}
      className={cls(className, { disabled })}
      style={wrapperStyle}
      checked={checked}
      disabled={disabled}
    >
      <div className="checkbox-container">
        <label
          htmlFor={checkboxId}
          className="checkbox-label-wrapper"
          id={`${checkboxId}-checkbox-label`}
          data-testid="checkbox"
        >
          <input
            ref={inputRef}
            id={checkboxId}
            data-testid={dataTestId}
            data-pendo-feature={dataPendoFeature}
            className="custom-checkbox"
            name={name}
            type="checkbox"
            checked={checked}
            onClick={(e) => {
              e.stopPropagation()
            }}
            onChange={(e) => {
              if (!disabled && onChange) {
                if (preventDefault) {
                  e.preventDefault()
                }
                onChange(e)
              }
            }}
            disabled={disabled}
            readOnly={readOnly}
            aria-checked={indeterminate ? 'mixed' : checked}
          />
          <span className="checkbox-custom">
            {indeterminate ? (
              <div
                className={cls('minus-icon', { disabled })}
                style={{ backgroundColor: minusIconBackgroundColor }}
                aria-hidden="true"
              />
            ) : (
              <Check
                width="9px"
                height="7px"
                className="checkbox-icon"
                fill={checked ? (disabled ? 'var(--ds-gray-5)' : ON_FILL_ICON) : 'transparent'}
                aria-hidden="true"
              />
            )}
          </span>
          {labelText && <span className="checkbox-label typography-body-1">{labelText}</span>}
        </label>
      </div>
      {InfoPopupComponent && (
        <Tooltip
          overlayStyle={{
            maxWidth: '250px',
            whiteSpace: 'normal',
          }}
          text={InfoPopupComponent}
        >
          <ActionIcon
            id={tooltipId}
            Icon={Info}
            iconProps={{
              width: '18px',
              height: '18px',
            }}
            containerStyle={{ marginLeft: '6px' }}
          />
        </Tooltip>
      )}
    </CheckboxWrapper>
  )
}

export default Checkbox
